// client/src/components/pages/LexiStar/Drivers/AllocationBoard.tsx (converted from .jsx, ST-00204)
//
// Driver–vehicle allocation board (LS-09 §5.9; FR-DRV-002/003): trucks as
// rows, days as columns, driver chips across the span they hold the truck.
// A driver on two trucks at overlapping times is ringed in red (two drivers on
// one truck cannot happen — the database refuses it). Beside it: drivers with
// no truck in the window, and the "Who was driving?" lookup (registration +
// time → driver, source of evidence, CSV export).
//
// ST-00204: the window picker is full width on a phone (its own 16rem from sm), where it opens
// the shared bottom sheet; "At" is the shared date + time field (StatusBoard/atoms).

import { useCallback, useEffect, useMemo, useState, type ComponentType } from 'react';
import { FaPlus, FaSearch, FaDownload } from 'react-icons/fa';
import UnifiedDateRangePicker from '../../../common/UnifiedDateRangePicker';
import { NOW_TIME_PRESETS } from '../../../common/pickerLogic';
import ThemedSelectJs from '../../../common/ThemedSelect';
import type { ThemedSelectProps } from '../../../../types/commonComponents';
import { DateTimeField, inputClasses, cardClasses } from '../StatusBoard/atoms';
import { fmtDateTime as fmtDateTimeJs } from '../StatusBoard/format';
import { pickList as pickListJs } from '../../../../services/lexistarStatusBoard';
import { listAllocations as listAllocationsJs } from './driversApi';
import { errorText as errorTextJs } from './DriverDialog';
import AllocationEditorJs from './AllocationEditor';
import {
  timelineBars as timelineBarsJs,
  doubleAllocatedDrivers as doubleAllocatedDriversJs,
  reachableChannels as reachableChannelsJs,
  channelLabel as channelLabelJs,
  evidenceCsv as evidenceCsvJs,
  spanOf as spanOfJs,
} from './driverLogic';

type Id = string | number;

/** A driver–truck allocation (GET /lexistar/allocations). */
export interface Allocation {
  id: Id;
  asset_id: Id;
  driver_id: Id;
  driver_name?: string;
  driver_tag?: string | null;
  from?: string;
  to?: string | null;
  source?: string;
  created_by?: string | null;
  cancelled?: boolean;
}

export interface BoardAsset {
  id: Id;
  registration: string;
}

export interface BoardDriver {
  id: Id;
  name: string;
  active?: boolean;
  [key: string]: unknown;
}

interface TimelineBar {
  allocation: Allocation;
  left: number;
  width: number;
}

interface LookupResult {
  allocation: Allocation | null;
  asset: BoardAsset | undefined;
  at: string;
}

// Typed views of the JS modules.
const timelineBars = timelineBarsJs as unknown as (list: Allocation[], start: number, end: number) => TimelineBar[];
const doubleAllocatedDrivers = doubleAllocatedDriversJs as unknown as (list: Allocation[]) => Set<Id>;
const reachableChannels = reachableChannelsJs as unknown as (driver: BoardDriver) => string[];
const channelLabel = channelLabelJs as unknown as (channel: string) => string;
const evidenceCsv = evidenceCsvJs as unknown as (x: {
  registration: string;
  at: string;
  allocation: Allocation | null;
}) => string;
const spanOf = spanOfJs as unknown as (a: Allocation) => { fromMs: number; toMs: number };
const listAllocations = listAllocationsJs as unknown as (params: Record<string, unknown>) => Promise<unknown>;
const errorText = errorTextJs as unknown as (err: unknown, fallback: string) => string;
const pickList = pickListJs as unknown as <T>(res: unknown, ...keys: string[]) => T[];
const fmtDateTime = fmtDateTimeJs as unknown as (iso: string | null | undefined) => string;
const ThemedSelect = ThemedSelectJs as unknown as ComponentType<ThemedSelectProps>;
const AllocationEditor = AllocationEditorJs as unknown as ComponentType<{
  assetId?: Id | null;
  driverId?: Id | null;
  drivers?: BoardDriver[];
  assets?: BoardAsset[];
  defaultFrom?: Date | null;
  onCancel: () => void;
  onSaved: () => void;
}>;

const DAY = 86400000;

const dayStart = (d: number | Date): Date => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};

function WhoWasDriving({ assets }: { assets: BoardAsset[] }) {
  const [asset, setAsset] = useState('');
  const [at, setAt] = useState<Date>(() => new Date());
  const [result, setResult] = useState<LookupResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const lookup = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await listAllocations({ asset_id: asset, at: at.toISOString() });
      const hit =
        pickList<Allocation>(res, 'allocations').find((a) => {
          if (a.cancelled) return false;
          const { fromMs, toMs } = spanOf(a);
          return fromMs <= at.getTime() && at.getTime() < toMs;
        }) || null;
      setResult({
        allocation: hit,
        asset: assets.find((a) => a.id === asset),
        at: at.toISOString(),
      });
    } catch (err) {
      setError(errorText(err, 'The lookup failed'));
    } finally {
      setBusy(false);
    }
  };

  const csvHref = result
    ? `data:text/csv;charset=utf-8,${encodeURIComponent(
        evidenceCsv({
          registration: result.asset?.registration || '',
          at: result.at,
          allocation: result.allocation,
        })
      )}`
    : undefined;

  return (
    <section className={`${cardClasses} p-4 space-y-3`} aria-label="Who was driving?">
      <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
        Who was driving?
      </h3>
      <div>
        <label htmlFor="who-asset" className="block text-xs font-medium text-slate-600 mb-1">
          Truck
        </label>
        <ThemedSelect
          id="who-asset"
          value={asset}
          onChange={(e) => setAsset(String(e.target.value))}
          className={inputClasses}
        >
          <option value="">Choose a truck...</option>
          {assets.map((a) => (
            <option key={a.id} value={a.id}>
              {a.registration}
            </option>
          ))}
        </ThemedSelect>
      </div>
      <div>
        <label htmlFor="who-at" className="block text-xs font-medium text-slate-600 mb-1">
          At
        </label>
        <DateTimeField id="who-at" value={at} onChange={(d) => d && setAt(d)} presets={NOW_TIME_PRESETS} />
      </div>
      <button
        type="button"
        onClick={lookup}
        disabled={!asset || busy}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold text-white rounded-lg bg-primary-500 hover:bg-primary-600 disabled:bg-slate-400"
      >
        <FaSearch size={11} /> {busy ? 'Looking...' : 'Look up'}
      </button>
      {error && (
        <p role="alert" className="text-sm text-red-700">
          {error}
        </p>
      )}
      {result && (
        <div className="text-sm" role="status">
          {result.allocation ? (
            <>
              <p className="text-slate-900">
                <span className="font-semibold">{result.allocation.driver_name}</span>
                {result.allocation.driver_tag ? ` (${result.allocation.driver_tag})` : ''} was
                allocated to <span className="font-mono">{result.asset?.registration}</span>.
              </p>
              <p className="text-xs text-slate-500 mt-1">
                Evidence: {result.allocation.source} allocation{' '}
                {fmtDateTime(result.allocation.from)} →{' '}
                {result.allocation.to ? fmtDateTime(result.allocation.to) : 'open-ended'}, recorded
                by {result.allocation.created_by || 'unknown'}.
              </p>
            </>
          ) : (
            <p className="text-amber-800">No driver was allocated to that truck at that time.</p>
          )}
          <a
            href={csvHref}
            download={`who-was-driving-${result.asset?.registration || 'truck'}.csv`}
            className="inline-flex items-center gap-1 mt-2 text-xs font-medium text-primary-700 hover:underline"
          >
            <FaDownload size={9} /> Export evidence
          </a>
        </div>
      )}
    </section>
  );
}

export interface AllocationBoardProps {
  assets: BoardAsset[];
  drivers: BoardDriver[];
  canManage: boolean;
  onOpenDriver: (driverId: Id) => void;
  reloadKey?: number | string;
}

export default function AllocationBoard({ assets, drivers, canManage, onOpenDriver, reloadKey }: AllocationBoardProps) {
  const [start, setStart] = useState<Date>(() => new Date(dayStart(Date.now()).getTime() - DAY));
  const [end, setEnd] = useState<Date>(() => new Date(dayStart(Date.now()).getTime() + 5 * DAY));
  const [allocations, setAllocations] = useState<Allocation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Id | null>(null);
  const [tick, setTick] = useState(0);

  const windowStart = dayStart(start).getTime();
  const windowEnd = dayStart(end).getTime() + DAY;
  const days = useMemo(() => {
    const out: number[] = [];
    for (let t = windowStart; t < windowEnd; t += DAY) out.push(t);
    return out;
  }, [windowStart, windowEnd]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listAllocations({
        from: new Date(windowStart).toISOString(),
        to: new Date(windowEnd).toISOString(),
        limit: 1000,
      });
      setAllocations(pickList<Allocation>(res, 'allocations'));
      setError(null);
    } catch (err) {
      setError(errorText(err, 'Failed to load allocations'));
    } finally {
      setLoading(false);
    }
  }, [windowStart, windowEnd]);

  useEffect(() => {
    load();
  }, [load, reloadKey, tick]);

  const conflicts = useMemo(() => doubleAllocatedDrivers(allocations), [allocations]);
  const byAsset = useMemo(() => {
    const m = new Map<Id, Allocation[]>();
    allocations.forEach((a) => {
      const list = m.get(a.asset_id) || [];
      list.push(a);
      m.set(a.asset_id, list);
    });
    return m;
  }, [allocations]);
  const allocatedDrivers = useMemo(
    () => new Set(allocations.filter((a) => !a.cancelled).map((a) => a.driver_id)),
    [allocations]
  );
  const unallocated = drivers.filter((d) => d.active !== false && !allocatedDrivers.has(d.id));
  const nowPct = ((Date.now() - windowStart) / (windowEnd - windowStart)) * 100;

  return (
    <div className="grid grid-cols-1 xl:grid-cols-12 gap-4">
      <section
        className={`${cardClasses} xl:col-span-9 overflow-hidden`}
        aria-label="Allocation board"
      >
        <div className="px-4 py-3 border-b border-slate-200 flex flex-wrap items-center justify-between gap-3">
          <div className="w-full sm:w-64">
            <UnifiedDateRangePicker
              startDate={start}
              endDate={end}
              onStartDateChange={(d) => d && setStart(d)}
              onEndDateChange={(d) => d && setEnd(d)}
              bounds="all"
            />
          </div>
          <p className="text-xs text-slate-500">
            {conflicts.size > 0 ? (
              <span className="text-red-700 font-semibold">
                ! {conflicts.size} allocation{conflicts.size === 1 ? '' : 's'} put one driver on two
                trucks at once
              </span>
            ) : (
              'No conflicts in this window.'
            )}
          </p>
        </div>
        {error && (
          <p role="alert" className="px-4 py-2 text-sm text-red-700">
            {error}
          </p>
        )}
        <div className="overflow-x-auto">
          <div className="min-w-[720px]">
            <div className="flex border-b border-slate-200 bg-slate-50 text-[11px] font-semibold text-slate-500">
              <div className="w-36 flex-shrink-0 px-3 py-2">Truck</div>
              <div className="flex-1 flex">
                {days.map((d) => (
                  <div
                    key={d}
                    className="flex-1 px-2 py-2 border-l border-slate-200 whitespace-nowrap"
                  >
                    {new Date(d).toLocaleDateString('en-GB', {
                      weekday: 'short',
                      day: 'numeric',
                      month: 'short',
                    })}
                  </div>
                ))}
              </div>
            </div>
            {loading && allocations.length === 0 ? (
              <p className="px-4 py-6 text-sm text-slate-500">Loading allocations...</p>
            ) : assets.length === 0 ? (
              <p className="px-4 py-6 text-sm text-slate-500">No trucks in the asset register.</p>
            ) : (
              <ul aria-label="Trucks">
                {assets.map((a) => {
                  const bars = timelineBars(byAsset.get(a.id) || [], windowStart, windowEnd);
                  return (
                    <li
                      key={a.id}
                      className="border-b border-slate-100"
                      aria-label={a.registration}
                    >
                      <div className="flex items-stretch">
                        <div className="w-36 flex-shrink-0 px-3 py-2 flex items-center justify-between gap-1">
                          <span className="font-mono text-xs font-semibold text-slate-800 truncate">
                            {a.registration}
                          </span>
                          {canManage && (
                            <button
                              type="button"
                              onClick={() => setEditing(editing === a.id ? null : a.id)}
                              aria-label={`Allocate a driver to ${a.registration}`}
                              className="p-1 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100"
                            >
                              <FaPlus size={10} />
                            </button>
                          )}
                        </div>
                        <div className="relative flex-1 h-10">
                          {days.map((d, i) => (
                            <span
                              key={d}
                              className="absolute top-0 bottom-0 border-l border-slate-100"
                              style={{ left: `${(i / days.length) * 100}%` }}
                            />
                          ))}
                          {nowPct >= 0 && nowPct <= 100 && (
                            <span
                              className="absolute top-0 bottom-0 w-0.5 bg-primary-500/70"
                              style={{ left: `${nowPct}%` }}
                            />
                          )}
                          {bars.map((b) => {
                            const conflict = conflicts.has(b.allocation.id);
                            return (
                              <button
                                key={b.allocation.id}
                                type="button"
                                onClick={() => onOpenDriver(b.allocation.driver_id)}
                                className={`absolute top-2 bottom-2 rounded-md px-2 text-[11px] font-semibold text-left truncate ${conflict ? 'bg-surface text-red-800 ring-2 ring-red-500' : 'bg-slate-700 text-slate-50 hover:bg-slate-800'}`}
                                style={{ left: `${b.left}%`, width: `${b.width}%` }}
                                title={`${b.allocation.driver_name}: ${fmtDateTime(b.allocation.from)} → ${b.allocation.to ? fmtDateTime(b.allocation.to) : 'open-ended'}${conflict ? ' — also on another truck at the same time' : ''}`}
                              >
                                {conflict && <span aria-hidden="true">! </span>}
                                {b.allocation.driver_name}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                      {editing === a.id && (
                        <div className="px-3 pb-3">
                          <AllocationEditor
                            assetId={a.id}
                            drivers={drivers}
                            defaultFrom={new Date()}
                            onCancel={() => setEditing(null)}
                            onSaved={() => {
                              setEditing(null);
                              setTick((t) => t + 1);
                            }}
                          />
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      </section>
      <div className="xl:col-span-3 space-y-4">
        <section className={`${cardClasses} p-4`} aria-label="Drivers without a truck">
          <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
            No truck in this window ({unallocated.length})
          </h3>
          {unallocated.length === 0 ? (
            <p className="text-sm text-slate-500">Every active driver has a truck.</p>
          ) : (
            <ul className="space-y-1.5">
              {unallocated.map((d) => {
                const reach = reachableChannels(d);
                return (
                  <li key={d.id}>
                    <button
                      type="button"
                      onClick={() => onOpenDriver(d.id)}
                      className="w-full text-left text-sm hover:bg-slate-50 rounded px-1 py-0.5"
                    >
                      <span className="font-medium text-slate-900">{d.name}</span>
                      <span
                        className={`block text-xs ${reach.length ? 'text-slate-500' : 'text-amber-700'}`}
                      >
                        {reach.length
                          ? `Reachable: ${reach.map(channelLabel).join(', ')}`
                          : 'Not reachable yet'}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
        <WhoWasDriving assets={assets} />
      </div>
    </div>
  );
}
